/**
 * One row of the room's header card: a label in a narrow left column, what it
 * says beside it, and the whole row is the door into its slideout.
 *
 * The answer and "what the room is doing" were two full-width boxes that looked
 * alike and worked differently — one was a button end to end, the other opened
 * only from a tiny link — and each put its door at the far right edge, a
 * screen's width from the words it belonged to. One card, two rows, one way to
 * open either.
 *
 * A DIV WITH A ROLE, not a <button>. The answer is text somebody will want to
 * select and copy, and a drag that selects a sentence also fires a click; a
 * selection is taken as the reader reading, not asking to open anything.
 */
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';

export const LABEL = 'text-[11px] uppercase tracking-wide text-gray-400';

export default function HeaderRow({
  label,
  onOpen,
  title,
  testId,
  children,
}: {
  label: string;
  /** Open this row's slideout. Without it the row is just a row. */
  onOpen?: () => void;
  title?: string;
  testId?: string;
  children: ReactNode;
}) {
  const open = onOpen
    ? (e: MouseEvent<HTMLDivElement>) => {
        // Selecting text is reading, not a request to open.
        if (window.getSelection?.()?.toString()) return;
        e.preventDefault();
        onOpen();
      }
    : undefined;
  const key = onOpen
    ? (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }
    : undefined;

  return (
    <div
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={open}
      onKeyDown={key}
      title={title}
      data-testid={testId}
      className={`grid grid-cols-[3.75rem_minmax(0,1fr)] gap-x-3 px-3 py-2 text-left ${
        onOpen
          ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-indigo-400'
          : ''
      }`}
    >
      <div className={`${LABEL} pt-0.5`}>{label}</div>
      <div className="min-w-0">{children}</div>
    </div>
  );
}

/** The door's words, sitting at the end of the row's own details rather than
 *  pinned to the far edge — the row is the target; this says where it goes. */
export function Door({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <span className="whitespace-nowrap text-indigo-600 dark:text-indigo-300" data-testid={testId}>
      {children} ›
    </span>
  );
}
